export const BASE_PRODUCT_ID = {
  JOGAK_STICKER: 8213,
  PLUSH_DOLL: 8857,
  TTIBU_STICKER: 8923,
} as const;

export type FlexiBaseProductId = (typeof BASE_PRODUCT_ID)[keyof typeof BASE_PRODUCT_ID];

export const CUTLINER_OPTS = {
  /**
   * 이미지 프로세싱 시 사용하는 DPI
   */
  DPI_96: 96,
  /**
   * Binarization 전 Gaussian blur 처리 시 사용하는 sigma 값
   */
  BLUR_SIGMA: 20,
  /**
   * 인쇄용 파일 생성 시 사용하는 DPI
   */
  DPI_300: 300,

  /**
   * 웹 환경에서 사용하는 DPI
   */
  WEB_TO_DPI_300_RATIO: 3.125, // 300 dpi / 96 dpi

  /**
   * 이미지 외곽으로부터 윤곽선을 생성할 때 사용하는 오프셋 거리 기본 값 (mm)
   */
  DEFAULT_OFFSET_MM: 2,

  /**
   * 윤곽선 생성을 위해 이미지 프로세싱 시 사용하는 리사이즈 타겟 크기 (px)
   * 작게 리사이즈 하면 더 빠르게 처리되지만, 윤곽선 정확도가 떨어질 수 있음
   */
  DEFAULT_IMG_RESIZE: { width: 720, height: 720 },

  /**
   * 칼선에 포함되는 디자인 이미지의 최대 리사이즈 크기 (px)
   */
  IMG_SIZE_LIMIT: 1920,

  /**
   * 이미지 파일 크기 제한 (MB)
   */
  IMG_FILE_LIMIT_SIZE_MB: 10,

  /**
   * 윤곽선 생성을 위해 이미지 프로세싱 시 사용하는 리사이즈 타겟 크기 주변 여백 (px)
   * 너무 타이트하게 주면 윤곽선 생성시 윤곽선이 단일 path 화 되지 않고 모서리에 접하는 곳마다 path 가 분할되는 현상이 발생하 수 있어 마진이 필요함.
   */
  DEFAULT_IMG_RESIZE_MARGIN: 2,

  /**
   * 썸네일 SVG 생성 시 사용하는 여백 (px)
   */
  DEFAULT_THUMBNAIL_EXPAND_MARGIN: 30,

  /**
   * 윤곽선 생성을 위해 이미지 프로세싱 시 사용하는 이미지 여백을 제거할 것인지 여부
   */
  DEFAULT_TRIM: true,

  /**
   * 윤곽선 생성을 위해 이미지 프로세싱 시 사용하는 알파 채널 임계값
   * 0~255 사이의 값
   * 이 값 이상인 픽셀만 유효한 픽셀로 간주하고 이미지를 이진화 처리함.
   * 그라데이션 효과가 있는 이미지에서는 이 값을 너무 낮게 설정하면 윤곽선의 offset 이 너무 큰 것처럼 느껴질 수 있음.
   * 즉, 너무 희미한 그라데이션 효과까지 유효한 픽셀로 가정하게 됨.
   */
  DEFAULT_ALPHA_THRESHOLD: 25,

  /**
   * Path 샘플링 시 사용하는 기본 간격 (px)
   * 경계점 추출, 거리 계산 등에 사용
   */
  DEFAULT_SAMPLE_INTERVAL: 3,

  /**
   * 다리(bridge) 생성 시 사용하는 기본 폭 (px)
   * island path 간 연결 다리의 두께
   */
  DEFAULT_BRIDGE_WIDTH: 16,

  /**
   * OUTLINE 에서 의미 없는 작은 path를 무시하기 위한 최소 면적 (px^2)
   * 이 값 이하의 path는 결과에서 제거됨
   */
  MIN_IGNORE_PATH_AREA: 30, // 가로 세로 1 mm 이하의 면적은 무시 (300 dpi 기준)

  /**
   * Cutline 병합시에 path를 무시하기 위한 최소 면적 (px^2)
   * 이 값 이하의 path는 결과에서 제거됨
   */
  MIN_MERGE_FILTER_AREA: 30,

  /**
   * 다리(bridge) 길이 보정값 (px)
   * 연결 다리의 길이를 약간 더 늘려서 경계에 잘 닿도록 함
   */
  BRIDGE_LENGTH_ADDER: 4,

  /**
   * Clipper 라이브러리 polygon 변환 시 사용하는 스케일 팩터
   * 정수 좌표 변환을 위한 배율
   */
  CLIPPER_SCALE: 200,

  /**
   * SVG 기본 크기 (px)
   * width/height 미지정 시 사용
   */
  DEFAULT_SIZE: 900,

  /**
   * SVG viewBox를 width/height에 맞춰 정규화할지 여부
   * true면 항상 0 0 width height로 맞춤
   */
  NORMALIZE_VIEWPORT: true,

  /**
   * Clipper 라이브러리 polygon 변환 시 사용하는 평탄화 허용 오차
   * 평탄화 과정에서 너무 작은 오차를 무시하기 위한 값
   */
  CLIPPER_FLATTEN_TOLERENCE: 0.5,

  DEFAULT_STROKE_STYLE: {
    fillColor: '#ffffff',
    strokeColor: undefined,
    strokeWidth: undefined,
    strokeDashArray: undefined,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },

  CUTLINE_FULL_STYLE: {
    fill: 'none',
    stroke: 'rgb(255, 105, 180)',
    'stroke-width': '1',
  },

  CUTLINE_HALF_STYLE: {
    fill: 'none',
    stroke: 'rgb(86, 131, 255)',
    'stroke-width': '1',
    'stroke-dasharray': '4 2',
  },
  SMOOTH_OFFSET_ADDER: 60,

  DEFAULT_SHADOW: {
    dx: 0,
    dy: 6,
    stdDeviation: 6,
    color: '#000',
    opacity: 0.25,
    filterId: 'cutline-shadow',
  },
};

// 상품별 최소 여백(mm) 정의
export const MIN_SIDE_WITH_MARGIN_MM: Readonly<Record<FlexiBaseProductId, number>> = {
  [BASE_PRODUCT_ID.JOGAK_STICKER]: 30,
  [BASE_PRODUCT_ID.PLUSH_DOLL]: 60,
  [BASE_PRODUCT_ID.TTIBU_STICKER]: 20,
};
